import { useState } from "react";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { type ApPoLineItem } from "@/accounts-payable/types";
import { formatDollars } from "@/accounts-payable/utils";
import { RecordField } from "./record-fields";
/** How many line items show before "Show all". */
const LINE_ITEM_PREVIEW = 3;

/**
 * Line items under a "Line items (N)" label, across the card's full width:
 * description over its SKU, unit cost flush right, a divider between items.
 * A line carrying an item the run is creating gets a "New item" badge.
 * Long lists show the first few behind "Show all".
 */
export const LineItemList = ({ items }: { items: ApPoLineItem[] }) => {
  const [showAll, setShowAll] = useState(false);
  const overflow = items.length > LINE_ITEM_PREVIEW;
  const visible =
    showAll || !overflow ? items : items.slice(0, LINE_ITEM_PREVIEW);

  return (
    <RecordField label={`Line items (${items.length})`} fullWidth>
      <Stack className="w-full" align="start" gap={2}>
        <Stack align="start" gap={0} className="w-full divide-y divide-line">
          {visible.map((it, i) => (
            <Stack
              key={i}
              direction="row"
              justify="between"
              gap={4}
              align="start"
              className="w-full py-2 first:pt-0 last:pb-0"
            >
              <Stack align="start" gap={0} className="min-w-0">
                <Text>
                  {it.quantity} × {it.description}
                </Text>
                <Stack direction="row" gap={1} align="center">
                  <Text variant="caption" tone="muted" className="font-mono">
                    {it.sku}
                  </Text>
                  {it.new_pricebook_item_code && (
                    <Badge variant="secondary" color="information" size="sm">
                      New item
                    </Badge>
                  )}
                </Stack>
              </Stack>
              <Text className="flex-none tabular-nums">
                {formatDollars(it.unit_cost)}
              </Text>
            </Stack>
          ))}
        </Stack>
        {overflow && (
          <Button
            variant="link"
            className="h-auto p-0"
            aria-expanded={showAll}
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? "Show fewer" : `Show all ${items.length}`}
          </Button>
        )}
      </Stack>
    </RecordField>
  );
};
